import { useState, useMemo } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, CheckCircle2, AlertTriangle } from "lucide-react";
import { money, qty } from "@/lib/poshanConstants";

/**
 * Item-wise Consumption (per student) — classwise tabbed display.
 * For each class tab, shows: item | quantity per student (+ total) | cost per student.
 * Plus a Cost Verification card: Expected Total (Students × Rate) vs Actual Items Total.
 */
export default function ItemwiseConsumption({ breakdown }) {
  const [tab, setTab] = useState("balVatika");

  const classesWithStudents = useMemo(
    () => (breakdown?.perClass || []).filter((c) => c.count > 0),
    [breakdown],
  );

  // Auto-pick first class with students
  useMemo(() => {
    if (classesWithStudents.length && !classesWithStudents.find((c) => c.key === tab)) {
      setTab(classesWithStudents[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classesWithStudents.map((c) => c.key).join(",")]);

  if (!breakdown || breakdown.mealType === "manual") return null;
  if (classesWithStudents.length === 0) {
    return (
      <section className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)]" data-testid="itemwise-empty">
        <div className="flex items-center gap-2 text-ink-700">
          <Package className="h-4 w-4 text-clay" />
          <h3 className="font-heading text-lg font-semibold">Item-wise Consumption</h3>
        </div>
        <p className="mt-3 text-sm text-ink-700">Enter attendance for at least one class to see per-student item breakdown.</p>
      </section>
    );
  }

  return (
    <section
      data-testid="itemwise-consumption"
      className="rounded-xl border border-border bg-white p-6 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-8"
    >
      <div className="mb-5 flex items-center gap-2">
        <Package className="h-4 w-4 text-clay" />
        <h3 className="font-heading text-xl font-semibold text-ink-900">Item-wise Consumption</h3>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-sand-100 p-1 flex-wrap h-auto">
          {classesWithStudents.map((c) => (
            <TabsTrigger
              key={c.key}
              value={c.key}
              data-testid={`itemwise-tab-${c.key}`}
              className="data-[state=active]:bg-clay data-[state=active]:text-white"
            >
              {c.label}
              <span className="ml-1.5 rounded-full bg-white/80 px-1.5 text-[10px] font-mono text-clay">{c.count}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {classesWithStudents.map((c) => {
          const expected = Number((c.count * c.perStudent).toFixed(2));
          const actualPerStudent = c.lines.reduce((s, l) => s + l.costPerStudent, 0);
          const actual = Number((c.count * actualPerStudent).toFixed(2));
          const matches = Math.abs(expected - actual) < 0.05;
          return (
            <TabsContent key={c.key} value={c.key} className="mt-5">
              <div className="overflow-x-auto rounded-lg border border-border" data-testid={`itemwise-table-${c.key}`}>
                <table className="w-full min-w-[640px] border-collapse text-sm">
                  <thead className="bg-sand-100 text-left text-ink-700">
                    <tr>
                      <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Item</th>
                      <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Per Student</th>
                      <th className="p-3 text-xs font-semibold uppercase tracking-[0.16em]">Total ({c.count})</th>
                      <th className="p-3 text-right text-xs font-semibold uppercase tracking-[0.16em]">Cost / Student</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.lines.map((l) => {
                      const qPerStu = l.costMode === "flat" ? null : l.qty;
                      const totalQty = qPerStu === null ? null : qPerStu * c.count;
                      return (
                        <tr key={`${c.key}-${l.item}`} className={`border-t border-border ${l.isLadoo ? "bg-ochre/5" : ""}`}>
                          <td className="p-3 font-medium text-ink-900">
                            {l.item}
                            {l.isLadoo && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-ochre">Ladoo</span>}
                            {l.isFree && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-moss-dark">Free</span>}
                          </td>
                          <td className="p-3 text-ink-700 font-mono">
                            {qPerStu === null ? "—" : `${qty(qPerStu)} ${l.unit}`}
                          </td>
                          <td className="p-3 text-ink-700 font-mono">
                            {totalQty === null ? "—" : `${qty(totalQty)} ${l.unit}`}
                          </td>
                          <td className="p-3 text-right font-semibold text-clay">
                            {l.isFree ? <span className="text-moss-dark">FREE</span> : money(l.costPerStudent)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-border bg-sand-100">
                      <th className="p-3 text-left text-xs font-semibold uppercase tracking-[0.16em] text-ink-700" colSpan={3}>Total Items / Student</th>
                      <th className="p-3 text-right text-clay">{money(actualPerStudent)}</th>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                data-testid={`cost-verification-${c.key}`}
                className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                  matches ? "border-moss bg-moss/10 text-moss-dark" : "border-clay bg-clay-50 text-clay-dark"
                }`}
              >
                <div className="flex items-start gap-2.5">
                  {matches ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  ) : (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="font-semibold uppercase tracking-wider text-xs">Cost Verification</p>
                    <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
                      <span className="text-ink-700">Expected Total (Students × Rate):</span>
                      <b className="text-right">{money(expected)}</b>
                      <span className="text-ink-700">Actual Items Total:</span>
                      <b className="text-right">{money(actual)}</b>
                      {!matches && (
                        <>
                          <span className="text-ink-700">Difference:</span>
                          <b className="text-right">{money(Math.abs(actual - expected))}</b>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </section>
  );
}
